import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, User, Phone, MapPin, Briefcase, Award, ArrowRight, FileText, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth, api } from '../App';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';

const CATEGORIES = ['Criminal', 'Civil', 'Corporate', 'Family', 'Intellectual Property', 'Cyber Law', 'Property', 'Tax', 'Immigration'];
const LANGUAGES = ['English', 'Hindi', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Bengali', 'Marathi', 'Gujarati', 'Punjabi'];

const LawyerRegisterPage = () => {
  const navigate = useNavigate();
  const { register: registerUser } = useAuth();
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  
  const [formData, setFormData] = useState({
    // User details
    name: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    location: '',
    otp: '',
    // Lawyer details
    bar_council_number: '',
    qualification: '',
    years_of_experience: '',
    specializations: [],
    languages: ['English'],
    consultation_fee: '',
    bio: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleSpecialization = (spec) => {
    setFormData(prev => ({
      ...prev,
      specializations: prev.specializations.includes(spec)
        ? prev.specializations.filter(s => s !== spec)
        : [...prev.specializations, spec]
    }));
  };

  const toggleLanguage = (lang) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter(l => l !== lang)
        : [...prev.languages, lang]
    }));
  };

  const sendEmailOtp = async () => {
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setSendingOtp(true);
    try {
      const res = await api.post('/auth/send-email-otp', { email: formData.email });
      setOtpSent(true);
      toast.success('OTP sent to your email!');
      if (res.data.test_otp) {
        toast.info(`Test OTP: ${res.data.test_otp}`, { duration: 15000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyEmailOtp = async () => {
    if (!formData.otp || formData.otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    
    try {
      await api.post('/auth/verify-email-otp', { email: formData.email, otp: formData.otp });
      setOtpVerified(true);
      toast.success('Email verified successfully!');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid OTP');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (step === 2) {
      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
      if (formData.password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      setStep(3);
      return;
    }

    if (step === 3) {
      if (formData.specializations.length === 0) {
        toast.error('Please select at least one specialization');
        return;
      }
      if (!formData.consultation_fee || parseFloat(formData.consultation_fee) <= 0) {
        toast.error('Please enter a valid consultation fee');
        return;
      }

      setLoading(true);
      try {
        // Register user
        await registerUser({
          name: formData.name,
          email: formData.email,
          mobile: formData.mobile,
          password: formData.password,
          location: formData.location,
          role: 'lawyer'
        });

        // Create lawyer profile
        await api.post('/lawyers/profile', {
          bar_council_number: formData.bar_council_number,
          qualification: formData.qualification,
          years_of_experience: parseInt(formData.years_of_experience),
          specializations: formData.specializations,
          languages: formData.languages,
          consultation_fee: parseFloat(formData.consultation_fee),
          bio: formData.bio
        });

        toast.success('Registration successful! Your profile is pending admin approval.');
        navigate('/lawyer/dashboard');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Registration failed');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="container-app">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-2xl mx-auto"
        >
          <Link to="/" className="text-2xl font-bold text-slate-900" data-testid="logo-link">
            Legal<span className="text-amber-600">Hub</span>
          </Link>
          
          <h1 className="mt-8 text-3xl font-bold text-slate-900">Register as a Lawyer</h1>
          <p className="mt-2 text-slate-500">Join our platform and connect with clients</p>

          {/* Progress Steps */}
          <div className="mt-6 flex items-center gap-2">
            {[1, 2, 3].map((s, idx) => (
              <React.Fragment key={s}>
                <div className={`flex items-center gap-2 ${step >= s ? 'text-amber-600' : 'text-slate-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= s ? 'bg-amber-100' : 'bg-slate-100'}`}>
                    {s === 1 && otpVerified ? <CheckCircle className="w-5 h-5 text-emerald-600" /> : s}
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">
                    {s === 1 ? 'Verify Email' : s === 2 ? 'Account' : 'Profile'}
                  </span>
                </div>
                {idx < 2 && <div className="flex-1 h-px bg-slate-200"></div>}
              </React.Fragment>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="mt-8 bg-white rounded-2xl p-8 shadow-sm border border-slate-200">
            {step === 1 && (
              <div className="space-y-5">
                <h2 className="text-xl font-semibold text-slate-900">Email Verification</h2>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className="pl-10"
                      required
                      disabled={otpSent}
                      data-testid="lawyer-email-input"
                    />
                  </div>
                </div>

                {!otpSent ? (
                  <Button 
                    type="button"
                    onClick={sendEmailOtp}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full py-6"
                    disabled={sendingOtp}
                    data-testid="lawyer-send-otp-btn"
                  >
                    {sendingOtp ? 'Sending...' : 'Send Verification Code'}
                  </Button>
                ) : (
                  <>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700">
                      <p>We've sent a 6-digit verification code to <strong>{formData.email}</strong></p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Enter Verification Code</label>
                      <Input
                        type="text"
                        name="otp"
                        value={formData.otp}
                        onChange={handleChange}
                        placeholder="Enter 6-digit code"
                        maxLength={6}
                        className="text-center text-xl tracking-widest"
                        required
                        data-testid="lawyer-otp-input"
                      />
                      <button 
                        type="button" 
                        onClick={sendEmailOtp}
                        className="text-sm text-amber-600 mt-2 hover:underline"
                        disabled={sendingOtp}
                      >
                        Resend Code
                      </button>
                    </div>
                    <Button 
                      type="button"
                      onClick={verifyEmailOtp}
                      className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-full py-6"
                      data-testid="lawyer-verify-otp-btn"
                    >
                      Verify Email
                    </Button>
                  </>
                )}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <h2 className="text-xl font-semibold text-slate-900">Account Details</h2>
                
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <span>Email verified: <strong>{formData.email}</strong></span>
                </div>
                
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Full Name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className="pl-10"
                        required
                        data-testid="lawyer-name-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Mobile Number (Optional)</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        type="tel"
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleChange}
                        placeholder="10-digit mobile (optional)"
                        className="pl-10"
                        maxLength={10}
                        data-testid="lawyer-mobile-input"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Location</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="City, State"
                      className="pl-10"
                      required
                      data-testid="lawyer-location-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Minimum 6 characters"
                        className="pl-10 pr-10"
                        required
                        data-testid="lawyer-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Confirm Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="Confirm password"
                        className="pl-10"
                        required
                        data-testid="lawyer-confirm-password-input"
                      />
                    </div>
                  </div>
                </div>

                <Button 
                  type="submit"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full py-6"
                  data-testid="lawyer-step2-next-btn"
                >
                  Continue <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5">
                <h2 className="text-xl font-semibold text-slate-900">Professional Details</h2>
                
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Bar Council Registration Number</label>
                    <div className="relative">
                      <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        type="text"
                        name="bar_council_number"
                        value={formData.bar_council_number}
                        onChange={handleChange}
                        placeholder="e.g., BCI/12345/2020"
                        className="pl-10"
                        required
                        data-testid="lawyer-bar-council-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Qualification</label>
                    <div className="relative">
                      <Award className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        type="text"
                        name="qualification"
                        value={formData.qualification}
                        onChange={handleChange}
                        placeholder="e.g., LLB, LLM"
                        className="pl-10"
                        required
                        data-testid="lawyer-qualification-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Years of Experience</label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        type="number"
                        name="years_of_experience"
                        value={formData.years_of_experience}
                        onChange={handleChange}
                        placeholder="e.g., 5"
                        className="pl-10"
                        min="0"
                        required
                        data-testid="lawyer-experience-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Consultation Fee (₹)</label>
                    <Input
                      type="number"
                      name="consultation_fee"
                      value={formData.consultation_fee}
                      onChange={handleChange}
                      placeholder="e.g., 500"
                      min="0"
                      required
                      data-testid="lawyer-fee-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Specializations</label>
                  <div className="flex flex-wrap gap-2">
                    {CATEGORIES.map(cat => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => toggleSpecialization(cat)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                          formData.specializations.includes(cat)
                            ? 'bg-amber-600 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                        data-testid={`specialization-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Languages Spoken</label>
                  <div className="flex flex-wrap gap-2">
                    {LANGUAGES.map(lang => (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => toggleLanguage(lang)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                          formData.languages.includes(lang)
                            ? 'bg-slate-900 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                        data-testid={`language-${lang.toLowerCase()}`}
                      >
                        {lang}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Bio (Optional)</label>
                  <Textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleChange}
                    placeholder="Brief description about yourself and your practice..."
                    rows={4}
                    data-testid="lawyer-bio-input"
                  />
                </div>

                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> Your profile will be reviewed by our admin team. 
                    You will be notified once your profile is approved.
                  </p>
                </div>

                <Button 
                  type="submit"
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-full py-6"
                  disabled={loading}
                  data-testid="lawyer-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Submit Registration <ArrowRight className="w-5 h-5" />
                    </span>
                  )}
                </Button>
              </div>
            )}
          </form>

          <p className="mt-6 text-center text-slate-500">
            Already registered?{' '}
            <Link to="/login" className="text-amber-600 font-medium hover:text-amber-700">
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default LawyerRegisterPage;
